import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { LoaderSymbol, BusyIcon, runLoading } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { fmtLocalShort } from '../../utils/datetime';
import { useModalKeys } from '../../hooks/useModalKeys';

// ─────────────────────────────────────────────────────────────────────────
// External Data Import — toolbar popups (ALPACA parity)
//   • MailSetupDialog    — grid of the 4 alert events (MCL/SCL/Alert/MRL) with
//                          a per-row edit form (tbl_EmailAlertSetup).
//   • ServicePeriodDialog— the scheduled-run form (tbl_Transition_Period).
// Both open as modal popups from the External Data Import toolbar.
// ─────────────────────────────────────────────────────────────────────────

/** Shared lightweight modal shell. */
function Modal({ title, icon, onClose, width = 900, children }: {
  title: string; icon: string; onClose: () => void; width?: number; children: React.ReactNode;
}) {
  // Bespoke popup keyboard: Esc closes (standing rule 14). Rendered only while
  // open, so the hook is always active here — covers every popup using this shell.
  const modalKeys = useModalKeys(true, { onClose });
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} {...modalKeys}
        style={{ background: '#fff', borderRadius: 8, width, maxWidth: '94vw', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--ai-line)' }}>
          {/* token-exempt: #1F4E79 = EDI dialog heading navy (semantic, no --ai-* token). */}
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1F4E79' }}>
            <i className={`ti ${icon}`} style={{ marginRight: 8, color: 'var(--brand-accent, var(--ai-orange))' }} />{title}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--ai-muted)' }}>×</button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--ai-muted)', display: 'block', marginBottom: 4 };
const input: React.CSSProperties = { padding: '6px 10px', fontSize: 13, border: '1px solid var(--ai-line)', borderRadius: 4, width: '100%', boxSizing: 'border-box' };

// ── Mail Setup ─────────────────────────────────────────────────────────────

interface MailEvent {
  id: number; eventName: string; fromMail: string; hasPassword: boolean;
  subject: string; bodyOfText: string; mailServer: string; portNo: string;
  displayName: string; ssl: boolean; emailTo: string; emailCC: string; emailBCC: string;
}

export function MailSetupDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const [rows, setRows]       = useState<MailEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<MailEvent | null>(null);
  const [pwd, setPwd]         = useState('');
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ data?: MailEvent[] }>('/api/v1/mail-setup')
      .then(r => setRows(r.data?.data ?? []))
      .catch(() => notify.error('Failed to load Mail Setup.'))
      .finally(() => setLoading(false));
  }, [notify]);
  useEffect(load, [load]);

  const upd = <K extends keyof MailEvent>(k: K, v: MailEvent[K]) =>
    setEditing(e => (e ? { ...e, [k]: v } : e));

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await runLoading(api.put(`/api/v1/mail-setup/${editing.id}`, {
        fromMail: editing.fromMail, fromMailPassword: pwd, subject: editing.subject,
        bodyOfText: editing.bodyOfText, mailServer: editing.mailServer, portNo: editing.portNo,
        displayName: editing.displayName, ssl: editing.ssl, emailTo: editing.emailTo,
        emailCC: editing.emailCC, emailBCC: editing.emailBCC,
      }), { blocking: true, label: `Saving ${editing.eventName}…` });
      notify.success(`Mail Setup (${editing.eventName}) saved.`);
      setEditing(null); setPwd(''); load();
    } catch (e: unknown) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Save failed.');
    } finally { setSaving(false); }
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '6px 10px', fontSize: 12, fontWeight: 700, color: 'var(--ai-muted)', borderBottom: '2px solid var(--ai-line)', background: 'var(--ai-header-bg)' };
  const td: React.CSSProperties = { padding: '6px 10px', fontSize: 13, color: 'var(--ai-ink)', borderBottom: '1px solid var(--ai-line-soft)' };

  return (
    <Modal title="Email Alert — Mail Setup" icon="ti-mail-cog" onClose={onClose} width={editing ? 720 : 880}>
      {loading ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ai-muted)' }}><LoaderSymbol size={18} /> Loading…</div>
      ) : editing ? (
        <>
          {/* token-exempt: #1F4E79 = EDI dialog heading/link navy (semantic, no --ai-* token). */}
          <div style={{ fontWeight: 700, color: '#1F4E79', marginBottom: 10 }}>
            <button onClick={() => { setEditing(null); setPwd(''); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#1F4E79', marginRight: 6 }}>← Back</button>
            Event: {editing.eventName}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <div><label style={label}>From Address</label><input style={input} value={editing.fromMail} onChange={e => upd('fromMail', e.target.value)} /></div>
            <div><label style={label}>Display Name</label><input style={input} value={editing.displayName} onChange={e => upd('displayName', e.target.value)} /></div>
            <div><label style={label}>Mail Server</label><input style={input} value={editing.mailServer} onChange={e => upd('mailServer', e.target.value)} /></div>
            <div><label style={label}>Port</label><input style={input} value={editing.portNo} onChange={e => upd('portNo', e.target.value)} /></div>
            <div><label style={label}>Password {editing.hasPassword && <span style={{ color: 'var(--ai-green)', fontWeight: 400 }}>(set — blank keeps it)</span>}</label>
              <input style={input} type="password" value={pwd} placeholder={editing.hasPassword ? '••••••••' : ''} onChange={e => setPwd(e.target.value)} /></div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={editing.ssl} onChange={e => upd('ssl', e.target.checked)} /> Use SSL/TLS</label>
            </div>
            <div style={{ gridColumn: '1 / span 2' }}><label style={label}>To</label><input style={input} value={editing.emailTo} placeholder="a@lab.com; b@lab.com" onChange={e => upd('emailTo', e.target.value)} /></div>
            <div><label style={label}>CC</label><input style={input} value={editing.emailCC} onChange={e => upd('emailCC', e.target.value)} /></div>
            <div><label style={label}>BCC</label><input style={input} value={editing.emailBCC} onChange={e => upd('emailBCC', e.target.value)} /></div>
            <div style={{ gridColumn: '1 / span 2' }}><label style={label}>Subject</label><input style={input} value={editing.subject} onChange={e => upd('subject', e.target.value)} /></div>
            <div style={{ gridColumn: '1 / span 2' }}><label style={label}>Body</label>
              <textarea value={editing.bodyOfText} onChange={e => upd('bodyOfText', e.target.value)} style={{ ...input, minHeight: 120, fontFamily: 'monospace', fontSize: 12 }} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
            <button className="ca-btn" onClick={() => { setEditing(null); setPwd(''); }}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving}><BusyIcon busy={saving} icon="ti-device-floppy" /> Save</button>
          </div>
        </>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, width: 40 }}></th>
            <th style={th}>Event Name</th><th style={th}>From Mail</th><th style={th}>Mail Server</th>
            <th style={th}>Port</th><th style={th}>Display Name</th><th style={{ ...th, textAlign: 'center' }}>SSL</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ ...td, textAlign: 'center' }}>
                  {/* token-exempt: #1F4E79 = EDI dialog action navy (semantic, no --ai-* token). */}
                  <button title="Edit" onClick={() => { setEditing(r); setPwd(''); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#1F4E79', fontSize: 16 }}><i className="ti ti-edit" /></button>
                </td>
                <td style={td}>{r.eventName}</td><td style={td}>{r.fromMail || '—'}</td><td style={td}>{r.mailServer || '—'}</td>
                <td style={td}>{r.portNo || '—'}</td><td style={td}>{r.displayName || '—'}</td>
                <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={r.ssl} disabled readOnly /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ── Service Period ───────────────────────────────────────────────────────

interface ServicePeriod { id: number | null; scheduledDate: string; noOfDays: number; hours: number; minutes: number; lastRunDate: string | null; }

export function ServicePeriodDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const [s, setS] = useState<ServicePeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get<{ data?: Partial<ServicePeriod> }>('/api/v1/service-period')
      .then(r => { const d = r.data?.data ?? {}; setS({ id: d.id ?? null, scheduledDate: d.scheduledDate ?? '', noOfDays: d.noOfDays ?? 0, hours: d.hours ?? 8, minutes: d.minutes ?? 30, lastRunDate: d.lastRunDate ?? null }); })
      .catch(() => notify.error('Failed to load Service Period.'))
      .finally(() => setLoading(false));
  }, [notify]);

  const upd = <K extends keyof ServicePeriod>(k: K, v: ServicePeriod[K]) => setS(p => (p ? { ...p, [k]: v } : p));

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      await runLoading(api.put('/api/v1/service-period', {
        scheduledDate: s.scheduledDate || null, noOfDays: Number(s.noOfDays) || 0,
        hours: Number(s.hours) || 0, minutes: Number(s.minutes) || 0,
      }), { blocking: true, label: 'Saving Service Period…' });
      notify.success('Service Period saved.');
      onClose();
    } catch (e: unknown) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Service Period" icon="ti-clock-cog" onClose={onClose} width={460}>
      {loading || !s ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ai-muted)' }}><LoaderSymbol size={18} /> Loading…</div>
      ) : (
        <>
          <label style={label}>Scheduled Date</label>
          <input style={input} type="date" value={s.scheduledDate} onChange={e => upd('scheduledDate', e.target.value)} />
          <label style={{ ...label, marginTop: 12 }}>Number of Days</label>
          <input style={input} type="number" min={0} value={s.noOfDays} onChange={e => upd('noOfDays', Number(e.target.value))} />
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <div style={{ flex: 1 }}><label style={label}>Hour (0–23)</label>
              <input style={input} type="number" min={0} max={23} value={s.hours} onChange={e => upd('hours', Number(e.target.value))} /></div>
            <div style={{ flex: 1 }}><label style={label}>Minute (0–59)</label>
              <input style={input} type="number" min={0} max={59} value={s.minutes} onChange={e => upd('minutes', Number(e.target.value))} /></div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 12 }}>Last run: {s.lastRunDate ? fmtLocalShort(s.lastRunDate) : '— never —'}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button className="ca-btn" onClick={onClose}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving}><BusyIcon busy={saving} icon="ti-device-floppy" /> Save</button>
          </div>
        </>
      )}
    </Modal>
  );
}
